<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex shrink-0 items-center justify-between space-x-2">
            <h1 class="text-2xl font-semibold text-gray-700">Coupons</h1>
            <div class="flex items-center space-x-2">
                <UInput v-model="search" type="text" placeholder="Search..." icon="i-ph-magnifying-glass-duotone" />
                <UInputMenu
                    v-model="filters.partner"
                    :search="searchPartner"
                    class="w-52"
                    placeholder="Partner"
                    option-attribute="name"
                    leading-icon="i-ph-handshake-duotone"
                    trailing
                    nullable
                    by="id"
                />
                <UButton
                    label="Clear"
                    color="gray"
                    @click="clearFilters()"
                    class="text-center"
                />
                <UButtonGroup size="sm" orientation="horizontal">
                    <UButton @click="overlays.create = true" color="black" icon="i-ph-plus-bold">
                        Add Coupon
                    </UButton>
                    <UDropdown
                        :popper="{ placement: 'bottom-end' }"
                        :items="[[
                            {
                                label: 'Create Multiple',
                                icon: 'i-ph-copy-simple-duotone',
                                click: () => overlays.createBulk = true,
                            },
                        ]]"
                    >
                        <UButton icon="i-heroicons-chevron-down-20-solid" color="black" class="border-l border-white/30" />
                    </UDropdown>
                </UButtonGroup>
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="coupons"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-ticket-duotone', label: 'No coupons.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3' } }"
                class="h-full"
            >
                <template #partner-data="{ row }">
                    <div class="flex items-center space-x-2.5">
                        <Avatar bucket="media" folder="partners" size="sm" :placeholder="row.partner.name" :path="row.partner.avatar" />
                        <span>{{ row.partner.name }}</span>
                    </div>
                </template>
                <template #code-data="{ row }">
                    <div class="group">
                        <span class="group-hover:hidden">{{ row.code.replace(/./g, '●') }}</span>
                        <button @click="copy(row.code)" type="button" class="hidden group-hover:inline font-mono">{{ row.code }}</button>
                    </div>
                </template>
                <template #validity-data="{ row }">
                    <span class="font-mono">{{ row.validity }} + {{ row.grace_period_days }}</span>
                </template>
                <template #expiry-data="{ row }">
                    <span v-if="row.expires_at">{{ dayjs(row.expires_at).format('lll') }}</span>
                    <UBadge v-else color="orange" label="Never expires" variant="subtle" />
                </template>
                <template #redemptions-data="{ row }">
                    <div class="flex items-center space-x-4">
                        <span class="font-mono font-medium">{{ row.redemptions }}/{{ row.max_uses }}</span>
                        <UProgress :value="(100 * row.redemptions) / row.max_uses" />
                    </div>
                </template>
                <template #actions-data="{ row }">
                    <UDropdown :items="actions(row)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </template>
            </UTable>
        </div>

        <TablePagination :pagination="pagination" class="shrink-0" />

        <OverlayCouponCreateCoupon v-model:open="overlays.create" @reload="fetchData" />
        <OverlayCouponBulkCreateCoupons v-model:open="overlays.createBulk" @reload="fetchData" />
        <OverlayCouponRemoveCoupon v-model:open="overlays.remove" @reload="fetchData" :coupon="selectedCoupon" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import { useStorage } from '@vueuse/core'
import { UserRole } from '~/types/user'
import { watchDebounced } from '@vueuse/core'
import type { Coupon } from '~/types/coupon'
import type { ListingStorage } from '~/types/common'
import { SubscriptionSourceType } from '~/types/subscription'

dayjs.extend(LocalizedFormat)

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Coupons' })

const client = useSupabaseClient()
const pagination = usePagination()
const toast = useToast()

const { copy } = useClipboard({
    onCopy: () => {
        toast.add({
            title: 'Coupon code copied!',
            color: 'green',
            icon: 'i-ph-check-circle-duotone'
        })
    },
})

const loading = ref(false)
const search = ref('')
const filters = ref<{ [key: string]: any }>({})
const coupons = ref<Coupon[]>([])
const selectedCoupon = ref<Coupon>()

const overlays = reactive({
    create: false,
    createBulk: false,
    remove: false,
})

const columns = [
    { key: 'partner', label: 'Partner' },
    { key: 'code', label: 'Code' },
    { key: 'validity', label: 'Validity / Grace Period (days)' },
    { key: 'expiry', label: 'Expiry Date' },
    { key: 'redemptions', label: 'Redemptions' },
    { key: 'actions', class: 'w-px' },
]

const actions = (coupon: Coupon) => [
    [
        {
            label: 'View Customers',
            icon: 'i-ph-users-fill',
            click: () => viewCouponCustomers(coupon.id),
        },
        {
            label: 'Remove',
            icon: 'i-ph-trash-fill',
            iconClass: 'text-red-400',
            click: () => {
                selectedCoupon.value = coupon
                overlays.remove = true
            },
        },
    ],
]

onMounted(() => fetchData())
watchDebounced(search, () => fetchData(), { debounce: 600 })
watch(filters, () => fetchData(), { deep: true })
watch(
    () => pagination.from,
    () => fetchData(),
)

async function fetchData() {
    loading.value = true
    coupons.value = []
    pagination.setTotal(undefined)

    let query = client
        .from('coupons')
        .select(
            `
            id, code, validity, grace_period_days, max_uses, redemptions, metadata, expires_at, updated_at,
            partner:partners (id, name, avatar)
        `,
            { count: 'exact' },
        )
        .is('deleted_at', null)
        .range(pagination.from, pagination.to)

    if (search.value) {
        query = query.ilike('code', `%${search.value}%`)
    }

    if (filters.value.partner) {
        query = query.eq('partner_id', filters.value.partner.id)
    }

    const { count, data } = await query

    if (count && data) {
        coupons.value = data
        pagination.setTotal(count)
    }

    loading.value = false
}

async function searchPartner(q: string) {
    const { data, error } = await client
        .from('partners')
        .select('id, name')
        .is('deleted_at', null)
        .ilike('name', `%${q}%`)
        .order('name')

    if (error) {
        return []
    }

    return data
}

async function viewCouponCustomers(id: string) {
    const { data: subscriptions } = await client.from('subscriptions')
        .select('customer_id')
        .eq('source_type', SubscriptionSourceType.coupon)
        .eq('source_id', id)

    const ids = Array.from(new Set((subscriptions ?? []).map((subscription) => subscription.customer_id).filter(Boolean)))

    const storage = useStorage('customers-list', {} as ListingStorage)
    storage.value = {
        search: '',
        filters: [],
        ids,
    }

    navigateTo('/customers')
}

function clearFilters() {
    search.value = ''
    filters.value = {}
}
</script>
